import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../designsystem/KC_Button/KC_Button";
import "../styles.scss";
import {
  Categories,
  EmojiClickData,
  EmojiStyle,
  Theme
} from "../../emoji-picker-react/types/exposedTypes";
import EmojiPicker from "../../emoji-picker-react";
import { tt } from "../../../utils/i18n/i18n";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import PrizeImageForEditor from "../../../app/19_JetpackConfig/components/GiftsConfiguration/PrizeImageForEditor";

interface EmojiPickerWrapperProps {
  onEmojiSelect: (emoji: string) => void;
  theme?: Theme;
  initialEmoji?: string;
}

const EmojiPickerWrapper: React.FC<EmojiPickerWrapperProps> = (props) => {
  const theme = props.theme === undefined ? Theme.LIGHT : props.theme;
  const initialEmoji =
    props.initialEmoji === undefined ? "🎁" : props.initialEmoji;
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [selectedEmoji, setSelectedEmoji] = useState(initialEmoji);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setSelectedEmoji(emojiData.emoji);
    props.onEmojiSelect(emojiData.emoji);
    setShowPicker(false);
  };

  const CATEGORIES = [
    {
      category: Categories.SUGGESTED,
      name: tt("emoji_category_recent")
    },
    {
      category: Categories.FOOD_DRINK,
      name: tt("emoji_category_food_drink")
    },
    {
      category: Categories.SMILEYS_PEOPLE,
      name: tt("emoji_category_smileys")
    },
    {
      category: Categories.ANIMALS_NATURE,
      name: tt("emoji_category_animals_nature")
    },
    {
      category: Categories.ACTIVITIES,
      name: tt("emoji_category_activities")
    },
    {
      category: Categories.OBJECTS,
      name: tt("emoji_category_objects")
    },
    {
      category: Categories.FLAGS,
      name: tt("emoji_category_flags")
    }
  ];

  const isCustomImage = useMemo(() => {
    return props.initialEmoji?.includes(".");
  }, [props.initialEmoji]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current != null &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onClick = useCallback(
    (e: any) => {
      e?.stopPropagation?.();

      if (isCustomImage) {
        return;
      }

      setShowPicker(!showPicker);
    },
    [isCustomImage]
  );

  useEffect(() => {
    setSelectedEmoji(initialEmoji);
  }, [initialEmoji]);

  return (
    <div className="d-flex flex-column position-relative gap-md align-items-center">
      <button
        onClick={onClick}
        className="btn btn-link text-decoration-none border text-center emoji-icon position-relative"
        type="button"
        aria-label="Open emoji picker"
      >
        <PrizeImageForEditor image={selectedEmoji} />
      </button>
      {showPicker && (
        <div
          ref={pickerRef}
          className="position-absolute"
          style={{
            zIndex: 999,
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: "8px"
          }}
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={theme}
            emojiStyle={EmojiStyle.TWITTER}
            categories={CATEGORIES}
            skinTonesDisabled={true}
            searchDisabled={false}
            autoFocusSearch={true}
            searchPlaceHolder={tt("search")}
          />
        </div>
      )}
      {!isCustomImage && (
        <div className="w-100">
          <KC_Button
            isInverted={true}
            className="ml-2 border d-flex"
            size={KC_ButtonSize.Medium}
            text={tt("modify_emoji")}
            type={KC_ButtonType.Button}
            showIconOnTheRight={true}
            icon={faPen}
            onClick={(e) => {
              e.stopPropagation();
              setShowPicker(!showPicker);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EmojiPickerWrapper;
